import { HttpClient } from '@angular/common/http';
import { DOCUMENT, ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, switchMap, take } from 'rxjs';
import { AIRLINE_LOGO_DEFAULT_ICON, AirlineLogosService, airlineCodeOfIcon } from './airline-logos.service';

/**
 * The site's icon registry: every `airline-logo-<code>` icon is the logo NugiosCore keeps for that airline (docs
 * features/flight-catalogue/qa.md R-FC38 ... R-FC42). The icon waits for the logos (asked once per session, on the first airline icon),
 * then draws the airline's logo, or the neutral airline icon when the core has none or the logos are not available (R-FC39 / R-FC40).
 * Every screen keeps binding `[svgIcon]="'airline-logo-' + code"` - no screen decides which airlines have a logo.
 */
@Injectable()
export class AirlineLogoIconRegistry extends MatIconRegistry {
  readonly #injector = inject(Injector);
  readonly #registered = new Set<string>();

  constructor() {
    super(
      inject(HttpClient, { optional: true })!,
      inject(DomSanitizer),
      inject(DOCUMENT, { optional: true }),
      inject(ErrorHandler)
    );
  }

  override getNamedSvgIcon(name: string, namespace = ''): Observable<SVGElement> {
    const code = namespace ? null : airlineCodeOfIcon(name);
    if (!code) return super.getNamedSvgIcon(name, namespace);
    // resolved lazily: the logo service's HttpClient runs the interceptors, which must not be built while the registry is
    return this.#injector
      .get(AirlineLogosService)
      .logos()
      .pipe(
        take(1),
        switchMap((logos) => {
          const logo = logos.get(code);
          if (!logo) return super.getNamedSvgIcon(AIRLINE_LOGO_DEFAULT_ICON);
          if (!this.#registered.has(name)) {
            this.addSvgIconLiteral(name, logo);
            this.#registered.add(name);
          }
          return super.getNamedSvgIcon(name);
        })
      );
  }
}
